import type { MouseEvent, ReactNode, JSX } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../types';
import { ModalContent } from './modal-content';
import { ModalHeader } from './modal-header';

export interface ModalProps
    extends CommonProps,
        VariantProps<typeof ModalStyles> {
    isShown: boolean;
    onClose: () => void;
    portalRoot?: string;
    children?: ReactNode;
}

export const ModalStyles = tv({
    slots: {
        overlay:
            'fixed top-0 z-50 flex h-screen w-screen justify-center backdrop-blur-2xl',
        modal: 'flex h-screen flex-col overflow-hidden pt-4 text-black md:mt-[10vh] md:h-auto md:w-full md:max-h-[80vh] md:max-w-lg md:rounded-md md:pb-8',
    },

    variants: {
        background: {
            solid: { modal: 'bg-white' },
            gradient: {
                modal: 'from-off-white-100 via-off-white-300 to-off-white-500 bg-gradient-to-t',
            },
        },
        size: {
            default: { overlay: '' },
            'fit-content': { overlay: 'items-center' },
        },
    },
    defaultVariants: {
        background: 'solid',
        size: 'default',
    },
});

const Modal = ({
    isShown,
    background,
    onClose,
    portalRoot = 'modal',
    className,
    children,
    size,
    testId = 'modal',
    ...props
}: ModalProps): JSX.Element | null => {
    const [modalContainer, setModalContainer] = useState<Element | null>(null);

    useEffect(() => {
        setModalContainer(
            document.querySelector(`[data-portal-root="${portalRoot}"]`)
        );
    }, []);

    useEffect(() => {
        if (isShown) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    }, [isShown]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    if (!modalContainer || !isShown) {
        return null;
    }

    const { modal, overlay } = ModalStyles();

    return createPortal(
        <div
            aria-modal="true"
            className={overlay({ size })}
            data-testid={testId}
            role="dialog"
            onClick={handleClick}
            {...props}
        >
            <div className={modal({ background, className })}>{children}</div>
        </div>,
        modalContainer
    );
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;

export { Modal };
